'use client';

import NextLink from 'next/link';
import { type JSX, useRef } from 'react';

import { useReactiveVar } from '@apollo/client/react';
import cx from 'classnames';
import { AnimatePresence, motion } from 'motion/react';

import { Typename } from '~/contentful/graphql/typename';
import Logo from '~/ui/assets/logos/offset.svg';
import { AnalyticsSpotlight, Event } from '~/ui/components/analytics';
import { Button, ButtonVariant } from '~/ui/components/button';
import { FullBleedCTAScope, FullBleedCTATrigger } from '~/ui/components/fullBleedCta';
import { Column, Grid } from '~/ui/components/grid';
import { useBreakpoint } from '~/ui/components/grid/useBreakpoint';
import { Image } from '~/ui/modules/image';
import { navigationOpenedVar } from '~/ui/modules/layout/navigation';
import spotlightStyles from '~/ui/modules/spotlight/spotlight.module.scss';
import videoStyles from '~/ui/styles/components/video.module.scss';
import { Breakpoint } from '~/ui/styles/grid';
import gridStyles from '~/ui/styles/grid.module.scss';
import spacingStyles from '~/ui/styles/spacing.module.scss';
import truncateStyles from '~/ui/styles/truncate.module.scss';
import typographyStyles from '~/ui/styles/typography.module.scss';
import { getArticleCta } from '~/ui/systems/article/get-article-cta';
import { getInternalLinkFromFragment } from '~/ui/systems/internalLink';

import styles from './hero.module.scss';
import { type getHome } from '../../query';

export type featureType = 'Community Based Archives';

export interface HomeHeroProps
  extends Pick<
    Awaited<ReturnType<typeof getHome>>,
    'spotlightImage' | 'spotlightVideo' | 'spotlightCtaLink'
  > {
  thematic: boolean;
  spotlightFeatureSlot?: JSX.Element;
}

export function HomeHero({
  spotlightImage,
  spotlightVideo,
  spotlightCtaLink,
  spotlightFeatureSlot,
  thematic,
}: HomeHeroProps) {
  const logoRef = useRef<HTMLDivElement>(null);
  const navigationOpened = useReactiveVar(navigationOpenedVar);
  const breakpoint = useBreakpoint();

  const { headline, eyebrow, cta, href } = getSpotlightCTA(spotlightCtaLink);

  return (
    <div className={cx(styles.heroContainer, spacingStyles.section)}>
      <div className={styles.mellonLogo}>
        <AnimatePresence initial={false}>
          {!navigationOpened && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <Grid>
                <Column
                  span={{ [Breakpoint.MOBILE]: 4, [Breakpoint.TABLET]: 5, [Breakpoint.DESKTOP]: 9 }}
                >
                  <div
                    ref={logoRef}
                    className={cx(styles.mellonLogoInner, gridStyles.overflowLeft)}
                  >
                    <Logo />
                  </div>
                </Column>
              </Grid>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div
        className={cx(spotlightStyles.spotlight, {
          [spotlightStyles.spotlightFullHeight]: breakpoint === Breakpoint.MOBILE,
        })}
      >
        <div
          className={cx(spotlightStyles.spotlightInner, {
            [spotlightStyles.spotlightFullHeight]: breakpoint !== Breakpoint.MOBILE,
          })}
        >
          {spotlightFeatureSlot ? (
            <>{spotlightFeatureSlot}</>
          ) : spotlightImage ? (
            <Image
              image={spotlightImage}
              className={cx(spotlightStyles.image, spotlightStyles.imageZoomOut)}
              aspectRatio="5/4"
              colSpan={{
                [Breakpoint.MOBILE]: 5,
                [Breakpoint.TABLET]: 7,
                [Breakpoint.DESKTOP]: 9,
              }}
              priority
            />
          ) : spotlightVideo ? (
            <video
              src={spotlightVideo.video?.url ?? undefined}
              className={cx(spotlightStyles.previewVideo, videoStyles.videoWrapper)}
              autoPlay
              controls={false}
              loop
              muted
              playsInline
              disablePictureInPicture
              disableRemotePlayback
            />
          ) : null}

          <div className={spotlightStyles.gradient} />

          <FullBleedCTAScope enabled={!thematic || breakpoint !== Breakpoint.MOBILE}>
            <Grid className={cx(spotlightStyles.content, styles.spotlightContent)}>
              <Column
                span={{ [Breakpoint.MOBILE]: 5, [Breakpoint.TABLET]: 5, [Breakpoint.DESKTOP]: 5 }}
                offsetLeft={{ [Breakpoint.LAPTOP]: 1, [Breakpoint.DESKTOP]: 2 }}
              >
                {eyebrow && <div className={spotlightStyles.eyebrow}>{eyebrow}</div>}
                {headline && (
                  <h2
                    className={cx(
                      typographyStyles.sectionTitle,
                      truncateStyles.truncate3,
                      spotlightStyles.headline,
                    )}
                  >
                    {headline}
                  </h2>
                )}
                <FullBleedCTATrigger>
                  <Button
                    variant={ButtonVariant.PRIMARY}
                    render={<NextLink href={href} />}
                    invert={breakpoint !== Breakpoint.MOBILE}
                    event={{
                      name: Event.SPOTLIGHT_CLICK,
                      data: {
                        spotlightType: AnalyticsSpotlight.HOMEPAGE,
                        spotlightTitle: headline,
                        spotlightLink: href,
                      },
                    }}
                  >
                    {cta}
                  </Button>
                </FullBleedCTATrigger>
              </Column>
            </Grid>
          </FullBleedCTAScope>
        </div>
      </div>

      <div
        ref={el => {
          if (!el) {
            return;
          }
          const onScroll = () => {
            const rect = el.getBoundingClientRect();
            const scrollTop = rect.top > 0 ? rect.top : 0;
            const scrollProgress = 1 - scrollTop / window.innerHeight;
            document.body.style.setProperty('--scroll-progress', scrollProgress.toString());
          };
          onScroll();
          window.addEventListener('scroll', onScroll);
          return () => window.removeEventListener('scroll', onScroll);
        }}
      />
    </div>
  );
}

function getSpotlightCTA(ctaLink: Awaited<ReturnType<typeof getHome>>['spotlightCtaLink']) {
  if (!ctaLink) {
    console.error('Home spotlight data missing');
    return {
      headline: '',
      eyebrow: '',
      cta: '',
      href: '',
    };
  }
  const href = getInternalLinkFromFragment(ctaLink);
  switch (ctaLink?.__typename) {
    case Typename.ARTICLE_PAGE:
      return {
        headline: ctaLink.title,
        eyebrow: ctaLink.eyebrow,
        cta: getArticleCta(ctaLink),
        href,
      };
    case Typename.EVENT_UPCOMING:
      return {
        headline: ctaLink.name,
        eyebrow: ctaLink.eventType,
        cta: 'See the event',
        href,
      };
    case Typename.FEATURE:
      return {
        headline: ctaLink.tagline,
        eyebrow: ctaLink.name,
        cta: 'Explore the Special Feature',
        href,
      };
    case Typename.GRANT_STORY:
      return {
        headline: ctaLink.title,
        eyebrow: ctaLink.granteeNameOverride || ctaLink.grant?.organization?.name,
        cta: 'Read the grant story',
        href,
      };
    case Typename.IDEA_PAGE:
      return {
        headline: ctaLink.question,
        eyebrow: ctaLink.name,
        cta: 'Explore the Idea',
        href,
      };
    default:
      console.error('Unable to resolve home spotlight data');
      return {
        headline: '',
        eyebrow: '',
        cta: '',
        href: '',
      };
  }
}
